import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { Toaster } from "sonner";
import Login from "@/pages/Login";
import Layout from "@/pages/Layout";
import Dashboard from "@/pages/Dashboard";
import Subscribers from "@/pages/Subscribers";
import UserPortal from "@/pages/UserPortal";
import Plans from "@/pages/Plans";
import IPPools from "@/pages/IPPools";
import Reports from "@/pages/Reports";
import OltDashboard from "@/pages/OltDashboard";
import Billing from "@/pages/Billing";
import OltInventory from "@/pages/OltInventory";
import NasInventory from "@/pages/NasInventory";
import RadiusLogs from "@/pages/RadiusLogs";
import ClientMap from "@/pages/ClientMap";
import Support from "@/pages/Support";
import Logs from "@/pages/Logs";
import Portal from "@/pages/Portal";
import NasStaff from "@/pages/NasStaff";
import NasStaffPermissions from "@/pages/NasStaffPermissions";
import Roles from "@/pages/Roles";
import NasLogins from "@/pages/NasLogins";
import CompanyProfile from "@/pages/CompanyProfile";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import NatLogs from "@/pages/NatLogs";

const Protected = ({ children }) => {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center font-mono text-xs text-muted-foreground">initializing session…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// Route guard: only super admin (master) may enter.
const MasterOnly = ({ children }) => {
  const { user } = useAuth();
  const isStaff = user?.type === "staff" || user?.is_staff === true || (user?.role || "").startsWith("nas-");
  if (isStaff) return <Navigate to="/" replace />;
  return children;
};

// Route guard: super admin + NAS admin (but not plain NAS employees).
const NasAdminOrAbove = ({ children }) => {
  const { user } = useAuth();
  const isStaff = user?.type === "staff" || user?.is_staff === true || (user?.role || "").startsWith("nas-");
  if (isStaff) {
    const raw = (user?.role || "").replace(/^nas-/, "");
    if (raw !== "nas_admin") return <Navigate to="/" replace />;
  }
  return children;
};

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <ThemedToaster />
          <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/portal" element={<Portal />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route
            path="/"
            element={
              <Protected>
                <Layout />
              </Protected>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="subscribers" element={<Subscribers />} />
            <Route path="users/:id" element={<UserPortal />} />
            <Route path="plans" element={<Plans />} />
            <Route path="ip-pools" element={<IPPools />} />
            <Route path="reports" element={<Reports />} />
            <Route path="billing" element={<Billing />} />
            <Route path="olts" element={<OltInventory />} />
            <Route path="olt-dashboard" element={<OltDashboard />} />
            <Route path="nas" element={<MasterOnly><NasInventory /></MasterOnly>} />
            <Route path="nas-staff" element={<NasAdminOrAbove><NasStaff /></NasAdminOrAbove>} />
            <Route path="nas-logins" element={<MasterOnly><NasLogins /></MasterOnly>} />
            <Route path="roles" element={<NasAdminOrAbove><Roles /></NasAdminOrAbove>} />
            <Route path="nas/:nasId/staff/:staffId/permissions" element={<NasAdminOrAbove><NasStaffPermissions /></NasAdminOrAbove>} />
            <Route path="radius" element={<RadiusLogs />} />
            <Route path="logs" element={<Logs />} />
            <Route path="nat-logs" element={<NatLogs />} />
            <Route path="map" element={<ClientMap />} />
            <Route path="support" element={<Support />} />
            <Route path="company" element={<MasterOnly><CompanyProfile /></MasterOnly>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  </ThemeProvider>
  );
}

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster position="top-right" theme={theme} richColors />;
}
